import {Box, Button, Dialog, DialogContent, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Slide, Stack, TextField, ThemeProvider} from "@mui/material";
import {TransitionProps} from "@mui/material/transitions";
import AppBar from '@mui/material/AppBar';
import LoadingButton from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, {ChangeEvent, useEffect, useState} from "react";
import {Pocket, Transaction, TransactionRequest} from "../logic/types";
import {createTheme} from "@mui/material/styles";
import {toLocalDateString} from "../logic/date";
import MoneyKindView from "./MoneyKindView";
import {useLifePlatformApiClient} from "../hooks/useLifePlatformApiClient";
import {changePocket} from "../logic/helpers";

interface Props {
  onAddedTransaction?: () => void
  onChangedTransaction?: () => void
  transaction?: Transaction
  open: boolean
  onClose: () => void
  date: Date
  pockets: Array<Pocket>
  initialFromPocketId?: string
  initialToPocketId?: string
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} children={props.children}/>;
});

export default function TransactionDialog(props: Props) {

  const handleClose = () => {
    props.onClose()
  }

  const {apiAddTransaction, apiChangeTransaction} = useLifePlatformApiClient();

  // Use a single state object for all fields
  interface TransactionDialogState {
    amount: string;
    description: string;
    date: string;
    fromPocket: string;
    toPocket: string;
    lastFromPocket: string;
    lastToPocket: string;
  }

  const [state, setState] = useState<TransactionDialogState>({
    amount: '',
    description: '',
    date: getDatePickerString(props.date),
    fromPocket: '',
    toPocket: '',
    lastFromPocket: '',
    lastToPocket: ''
  });

  const { amount, description, date, fromPocket, toPocket, lastFromPocket, lastToPocket } = state;

  useEffect(() => {
    if (!props.open) return

    if (props.transaction) {
      setState({
        amount: props.transaction.amount.toString(),
        description: props.transaction.description,
        date: props.transaction.paidAt.substring(0, 10),
        fromPocket: props.transaction.fromPocketId ?? props.initialFromPocketId ?? '',
        lastFromPocket: props.transaction.fromPocketId ?? props.initialFromPocketId ?? '',
        toPocket: props.transaction.toPocketId ?? props.initialToPocketId ?? '',
        lastToPocket: props.transaction.toPocketId ?? props.initialToPocketId ?? ''
      })
    } else {
      setState({
        amount: '',
        description: '',
        date: getDatePickerString(props.date),
        fromPocket: props.initialFromPocketId ?? '',
        lastFromPocket: props.initialFromPocketId ?? '',
        toPocket: props.initialToPocketId ?? '',
        lastToPocket: props.initialToPocketId ?? ''
      })
    }
  }, [props.open, props.transaction, props.date, props.initialFromPocketId, props.initialToPocketId])


  const theme = createTheme({
    palette: {
      primary: {
        main: '#6750A4',
      },
      background: {
        paper: '#FFFBFE',
        default: '#7D5260'
      }
    },
  });

  function getDatePickerString(date: Date): string {
    let month = ('00' + (date.getMonth() + 1)).substr(-2)
    let day = ('00' + date.getDate()).substr(-2)
    return `${date.getFullYear()}-${month}-${day}`
  }

  async function onClick() {
    if (props.transaction)
      await onChangeTransaction(props.transaction.id)
    else
      await onAddTransaction()
  }

  async function onAddTransaction() {
    const request: TransactionRequest = {
      amount: parseInt(amount),
      paidAt: toLocalDateString(new Date(date)),
      description: description,
      fromPocketId: fromPocket !== "" ? fromPocket : undefined,
      toPocketId: toPocket !== "" ? toPocket : undefined,
    }
    const added = await apiAddTransaction(request)
    if (added) {
      if (props.onAddedTransaction)
        props.onAddedTransaction()
      handleClose()
    }
  }

  async function onChangeTransaction(transactionID: string) {
    const request: TransactionRequest = {
      amount: parseInt(amount),
      intentId: props.transaction?.intentId,
      paidAt: toLocalDateString(new Date(date)),
      description: description,
      fromPocketId: fromPocket !== "" ? fromPocket : undefined,
      toPocketId: toPocket !== "" ? toPocket : undefined,
    }
    const changed = await apiChangeTransaction(transactionID, request)
    if (changed) {
      if (props.onChangedTransaction)
        props.onChangedTransaction()
      handleClose()
    }
  }

  // Handlers using setState
  function handleChangeAmount(event: ChangeEvent<HTMLInputElement>) {
    setState(prev => ({ ...prev, amount: event.target.value }));
  }
  function handleChangeDescription(event: ChangeEvent<HTMLInputElement>) {
    setState(prev => ({ ...prev, description: event.target.value }));
  }
  function handleChangeDate(event: ChangeEvent<HTMLInputElement>) {
    setState(prev => ({ ...prev, date: event.target.value }));
  }
  function setDescription(desc: string) {
    setState(prev => ({ ...prev, description: desc }));
  }

  const handleChangePockets = (from: string, to: string) => {
    const res = changePocket({...state, fromPocket: from, toPocket: to})
    setState(prev => ({
      ...prev,
      ...res,
    }));
  }

  const handleChangeFromPocket = (event: SelectChangeEvent) => {
    handleChangePockets(event.target.value as string, toPocket);
  };
  const handleChangeToPocket = (event: SelectChangeEvent) => {
    handleChangePockets(fromPocket, event.target.value as string);
  };

  function validateForm(): boolean {
    return parseInt(amount) > 0;
  }

  function mapPocketToMenuItem(pocket: Pocket) {
    return (
      <MenuItem key={pocket.id} value={pocket.id}>{pocket.title}</MenuItem>
    )
  }

  return (
    <Dialog
      fullScreen
      open={props.open}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <ThemeProvider theme={theme}>
        <AppBar
          style={{backgroundColor: "#6750A4"}}
          sx={{position: 'relative'}}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon/>
            </IconButton>
            <Typography sx={{ml: 2, flex: 1}} variant="h6" component="div">
              Транзакция
            </Typography>
            <LoadingButton color="inherit" onClick={onClick} disabled={!validateForm()}>
              {props.transaction ? 'Применить' : 'Добавить'}
            </LoadingButton>
          </Toolbar>
        </AppBar>

        <DialogContent>
          <Stack component="form" spacing={2} onSubmit={onAddTransaction}>
            <Stack direction="row" justifyContent="space-between">
              <TextField
                autoFocus
                label="Сумма"
                type="number"
                onChange={handleChangeAmount}
                value={amount}
              />

              <TextField
                label="Дата"
                type="date"
                onChange={handleChangeDate}
                value={date}
              />
            </Stack>

            <TextField
              label="Описание"
              type="text"
              onChange={handleChangeDescription}
              value={description}
            />

            <Box>
              <Button variant="text" onClick={() => setDescription("REWE")}>
                REWE
              </Button>

              <Button variant="text" onClick={() => setDescription("DM")}>
                DM
              </Button>

              <Button variant="text" onClick={() => setDescription("Bars")}>
                Bars
              </Button>

              <Button variant="text" onClick={() => setDescription("Amazon")}>
                Amazon
              </Button>

              <Button variant="text" onClick={() => setDescription("Cats")}>
                Cats
              </Button>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Из кошелька</InputLabel>
              <Select
                value={fromPocket}
                label="Из кошелька"
                onChange={handleChangeFromPocket}
              >
                <MenuItem value={''}><i>Не выбран</i></MenuItem>
                {props.pockets.map(mapPocketToMenuItem)}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>В кошелек</InputLabel>
              <Select
                value={toPocket}
                label="В кошелек"
                onChange={handleChangeToPocket}
              >
                <MenuItem value={''}><i>Не выбран</i></MenuItem>
                {props.pockets.map(mapPocketToMenuItem)}
              </Select>
            </FormControl>

            <MoneyKindView fromPocketId={fromPocket} toPocketId={toPocket}/>

          </Stack>
        </DialogContent>
      </ThemeProvider>
    </Dialog>
  );
}
